import React from 'react';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { FormControl, TextField } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SxProps } from '@mui/system';
import StyledInput from 'src/components/BeaconV2Components/common/StyledInput';
import color from 'src/utils/colors';

interface InputLabelProps {
  label?: string;
  textSx?: SxProps;
  required?: boolean;
}

export const InputLabel: React.FC<InputLabelProps> = ({ textSx, label, required }) => {
  return (
    <Text
      variant="h7"
      sx={{
        ...textSx,
        display: 'flex',
        lineHeight: '20px'
      }}
    >
      {label}
      {required && (
        <Text
          variant="body3"
          sx={{
            lineHeight: '18px',
            marginLeft: '2px'
          }}
        >
          *
        </Text>
      )}
    </Text>
  );
};

type RHFInputProps<T extends FieldValues> = InputLabelProps & {
  name: Path<T>;
  placeholder?: string;
  defaultValue?: string | number;
  filterRegex?: RegExp;
  style?: React.CSSProperties;
  type?: 'text' | 'number';
  min?: number;
  max?: number;
  multiline?: boolean;
};

export function ControlledInput<T extends FieldValues>(allProps: RHFInputProps<T>) {
  const { control } = useFormContext();
  const {
    name,
    placeholder,
    label,
    defaultValue = '',
    required = false,
    textSx = undefined,
    filterRegex,
    style,
    type = 'text',
    min,
    max,
    multiline,
    ...otherInputProps
  } = allProps;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const originalOnChange = field.onChange;

        const handleFilteredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = event.target.value;

          if (type === 'number') {
            // For empty input, set to empty string to allow clearing
            if (rawValue === '') {
              originalOnChange('');
              return;
            }

            // Prevent scientific notation characters (e, E, +, -)
            if (/[eE\+\-]/.test(rawValue)) {
              return; // Don't update if invalid characters are present
            }

            const numValue = parseFloat(rawValue);

            // Check if it's a valid number and within bounds
            if (!isNaN(numValue)) {
              if (min !== undefined && numValue < min) {
                originalOnChange(min.toString());
                return;
              }
              if (max !== undefined && numValue > max) {
                originalOnChange(max.toString());
                return;
              }
              originalOnChange(numValue.toString());
              return;
            }
            // If invalid number, don't update
            return;
          }

          // Text input handling
          let filteredValue = rawValue;
          if (filterRegex) {
            const disallowedCharsRegex = new RegExp(`[^${filterRegex.source}]`, 'g');
            filteredValue = rawValue.replace(disallowedCharsRegex, '');
          }
          originalOnChange(filteredValue);
        };

        return (
          <FormControl>
            {label && <InputLabel textSx={textSx} label={label} required={required} />}
            {multiline ? (
              <TextField
                {...field}
                type={type}
                value={field.value}
                onChange={handleFilteredChange}
                placeholder={placeholder}
                multiline
                variant="outlined"
                sx={{
                  width: '100%',
                  marginTop: label ? '8px' : undefined,
                  '& .MuiOutlinedInput-root': {
                    padding: '6px 14px',
                    border: '1px solid #e8e8ed',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    fontWeight: 400,
                    '& fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: color.blueGrey,
                    opacity: 1
                  }
                }}
                inputProps={{
                  style: {
                    padding: '0px',
                    ...style
                  }
                }}
                {...otherInputProps}
              />
            ) : (
              <StyledInput
                {...field}
                type={type}
                value={field.value}
                onChange={handleFilteredChange}
                placeholder={placeholder}
                min={min}
                max={max}
                style={{
                  width: '100%',
                  marginTop: label ? '8px' : undefined,
                  padding: '6px 14px',
                  boxSizing: 'border-box',
                  height: '32px',
                  // Hide number input spinners
                  ...(type === 'number' && {
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      '-webkit-appearance': 'none',
                      margin: 0
                    },
                    '&[type=number]': {
                      '-moz-appearance': 'textfield'
                    }
                  }),
                  ...style
                }}
                {...otherInputProps}
              />
            )}
          </FormControl>
        );
      }}
    />
  );
}
